import { useEffect, useMemo, useRef, useLayoutEffect } from "react";

// Raw drag distance required to trigger a refresh
const REFRESH_THRESHOLD = 150;
// Maximum visual travel of the spinner (rubber-band damping)
const MAX_TRAVEL = 60;

interface Options {
  refresh: () => Promise<void>;
}

const getScrollTop = () =>
  document.documentElement.scrollTop || document.body.scrollTop;

/**
 * iOS disables swipe-to-refresh on PWAs added to the home screen, so we have to compute it ourselves.
 */
export const useSwipeToRefresh = ({ refresh }: Options) => {
  const swipeRef = useRef<HTMLDivElement>(null);
  const spinnerRef = useRef<HTMLDivElement>(null);
  const refreshRef = useRef(refresh);
  useLayoutEffect(() => {
    refreshRef.current = refresh;
  });

  const requiresSwipeToRefresh = useMemo(() => {
    return (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      !!(navigator as any).standalone ||
      window.matchMedia("(display-mode: standalone)").matches ||
      process.env.NODE_ENV === "development"
    );
  }, []);

  useEffect(() => {
    if (!requiresSwipeToRefresh) return;

    let touchStartY = 0;
    let pulling = false;
    let shouldRefresh = false;
    let isRefreshing = false;

    const resetSpinner = (animated: boolean) => {
      const el = spinnerRef.current;
      if (!el) return;
      el.style.transition = animated
        ? "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease"
        : "none";
      el.style.transform = "";
      el.style.opacity = "0";
    };

    const touchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0].clientY;
      pulling = false;
      shouldRefresh = false;
    };

    const touchMove = (e: TouchEvent) => {
      if (isRefreshing) return;
      // Only engage pull-to-refresh when the page is scrolled to the very top
      if (getScrollTop() > 0) return;

      const delta = e.touches[0].clientY - touchStartY;
      if (delta <= 0) {
        if (pulling) {
          pulling = false;
          shouldRefresh = false;
          resetSpinner(false);
        }
        return;
      }

      pulling = true;
      shouldRefresh = delta >= REFRESH_THRESHOLD;

      // Rubber-band: fast at first, decelerates toward MAX_TRAVEL
      const progress = Math.min(delta / REFRESH_THRESHOLD, 1);
      const travel = MAX_TRAVEL * (1 - Math.pow(1 - progress, 2));
      const opacity = Math.pow(progress, 0.5);
      const angle = Math.floor(progress * 270);

      const el = spinnerRef.current;
      if (el) {
        el.style.transition = "none";
        el.style.transform = `translateY(${travel}px) rotate(${angle}deg)`;
        el.style.opacity = String(Math.min(opacity, 1));
      }
    };

    const touchEnd = () => {
      if (!pulling) return;
      pulling = false;

      if (shouldRefresh && !isRefreshing) {
        shouldRefresh = false;
        isRefreshing = true;

        const el = spinnerRef.current;
        if (el) {
          el.classList.add("animate");
          // Hold spinner in place while refreshing
          el.style.transition = "none";
          el.style.transform = `translateY(${MAX_TRAVEL}px)`;
          el.style.opacity = "1";
        }

        let settled = false;
        const hideSpinner = () => {
          if (settled) return;
          settled = true;
          isRefreshing = false;
          const el = spinnerRef.current;
          if (el) {
            el.classList.remove("animate");
            resetSpinner(true);
          }
        };
        // Guard against refresh() never settling (e.g. network hang)
        const timeoutId = setTimeout(hideSpinner, 10_000);
        refreshRef.current().finally(() => {
          clearTimeout(timeoutId);
          hideSpinner();
        });
      } else {
        shouldRefresh = false;
        resetSpinner(true);
      }
    };

    const touchCancel = () => {
      pulling = false;
      shouldRefresh = false;
      resetSpinner(false);
    };

    document.addEventListener("touchstart", touchStart, { passive: true });
    document.addEventListener("touchmove", touchMove, { passive: true });
    document.addEventListener("touchend", touchEnd, { passive: true });
    document.addEventListener("touchcancel", touchCancel, { passive: true });

    return () => {
      document.removeEventListener("touchstart", touchStart);
      document.removeEventListener("touchmove", touchMove);
      document.removeEventListener("touchend", touchEnd);
      document.removeEventListener("touchcancel", touchCancel);
    };
  }, [requiresSwipeToRefresh]);

  return { swipeRef, spinnerRef };
};
